import { useEffect, useMemo, useState } from 'react';

import { GridSortDirection } from '@mui/x-data-grid-pro';

import useDataGridTable from '@/shared/components/dataDisplay/DataGridTable/hooks/useDataGridTable';
import useTableColumnVisibility from '@/shared/components/dataDisplay/DataGridTable/hooks/useTableColumnVisibility';
import useSearchProducts from '@/shared/hooks/useSearchProducts';
import ProductVariant from '@/shared/types/products/productVariant.type';
import ShippingProfile from '@/shared/types/shipping/shippingProfile.type';

const productRequiredColumns: (keyof ProductVariant)[] = [
  'product_id',
  'product_category',
  'product_name',
  'actions',
];

const productVariantsRequiredColumns: (keyof ProductVariant)[] = [
  'product_id',
  'variant_id',
  'product_category',
  'product_name',
  'variant_name',
  'actions',
];

type Args = {
  shippingProfile: ShippingProfile;
};

const useAssignedProductsAvdVariantsTable = ({ shippingProfile }: Args) => {
  const { apiRef, model, requestBody } = useDataGridTable();

  const [level, setLevel] = useState<'products' | 'variants'>('variants');

  const [table, setTable] = useState({
    data: [] as ProductVariant[],
    totalRows: 0,
    isLoading: false,
  });

  const tableRequiredColumns = useMemo(() => {
    return level === 'products'
      ? productRequiredColumns
      : productVariantsRequiredColumns;
  }, [level]);

  const { productsResponse, tableColumnsList } = useSearchProducts({
    searchObject: {
      ...requestBody,
      sort_order: requestBody.sort_order as GridSortDirection,
      search: [
        {
          variant_id: {
            cop: ['in'],
            value: shippingProfile.product_variants.map(
              (item) => item.variant_id,
            ),
          },
        },
        ...requestBody.search,
      ],
    },
  });

  const tableColumnsState = useTableColumnVisibility({
    tableColumnsList: tableColumnsList || [],
    tableRequiredColumns,
  });

  useEffect(() => {
    setTable((prev) => ({ ...prev, isLoading: true }));

    if (productsResponse?.success) {
      const variants = productsResponse.data
        .flatMap((item) => item.variants)
        .filter((variant) =>
          shippingProfile.product_variants
            .map((item) => item.variant_id)
            .includes(variant.variant_id),
        );

      const defaultVariants = variants
        .filter((variant) => variant.variant_name === '_default')
        .map((variant) => ({ ...variant, variant_name: '' }));

      const variantsExceptDefault = variants.map(
        ({ variant_name, ...variant }) => ({
          ...variant,
          variant_name: variant_name === '_default' ? '' : variant_name,
        }),
      );

      if (level === 'products') {
        setTable((prev) => ({
          ...prev,
          data: defaultVariants,
          totalRows: productsResponse?.total_records || 0,
          isLoading: false,
        }));
      } else {
        setTable((prev) => ({
          ...prev,
          data: variantsExceptDefault,
          totalRows: variantsExceptDefault.length,
          isLoading: false,
        }));
      }
    }
  }, [
    level,
    productsResponse?.data,
    productsResponse?.success,
    productsResponse?.total_records,
    shippingProfile.product_variants,
  ]);

  return {
    model,
    table,
    level,
    apiRef,
    tableColumnsState,
    setLevel,
  };
};

export default useAssignedProductsAvdVariantsTable;
